import { useEffect, useRef, useState } from 'react';
import { useMapContext } from '@/context/MapContext';
import { TabsType } from '@/enums';
import { calculateBounds,getDistance } from '@/helpers';
import { useProjectContext } from '@/context/ProjectContext';
interface Props {
  map: google.maps.Map | null;
  isLoaded: boolean;
  selectedTab: string;
  isDrawModeOn: boolean;
}
const useMarketDrawing = ({
  map,
  isLoaded,
  selectedTab,
  isDrawModeOn,
}: Props) => {
  const [hasBoundsChanged, setHasBoundsChanged] = useState<boolean>(true);
  const [rectangleListener, setRectangleListener] =
    useState<google.maps.MapsEventListener | null>(null);
  const rectangleRef = useRef<google.maps.Rectangle | null>(null);

  const {
    currentCoordinates,
    setMarketRectangleBounds,
  } = useMapContext();
  const {
    project: {marketPolygons}
  } = useProjectContext();

  const clearRectangleRef = () => {
    if (rectangleRef.current) {
      rectangleRef.current.setMap(null);

      rectangleRef.current = null;
    }
    if (rectangleListener) {
      google.maps.event.removeListener(rectangleListener);
      setRectangleListener(null);
    }
  };


  const generateTriangle = () => {
    if(marketPolygons){ return; }
    const zoomLevel = map?.getZoom();
    const distance = getDistance(zoomLevel ?? 10);
    const newBounds = calculateBounds(currentCoordinates, distance);

    const rectangle = new google.maps.Rectangle({
      bounds: newBounds,
      editable: true,
      draggable: true,
      clickable: false,
    });
    const rectangleList = rectangle.addListener('bounds_changed', () => {
      setHasBoundsChanged((prevState) => !prevState);
    });

    setRectangleListener(rectangleList);
    setMarketRectangleBounds(newBounds);

    rectangleRef.current = rectangle;
    rectangleRef.current.setMap(map);
  }

  useEffect(() => {
    if (selectedTab === TabsType.market) {
      clearRectangleRef();
    }
  }, [currentCoordinates, selectedTab]);

  useEffect(() => {
    if (selectedTab === TabsType.market && isLoaded) {
      generateTriangle();
    }
  }, [currentCoordinates, isLoaded]);

  useEffect(() => {
    if (rectangleRef.current) {
      const bounds = rectangleRef.current.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        setMarketRectangleBounds({
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng(),
        });
      }
    }
  }, [hasBoundsChanged]);

  useEffect(() => {
    if (selectedTab === TabsType.market && isLoaded && !marketPolygons) {
      const zoomLevel = map?.getZoom();
      const distance = getDistance(zoomLevel ?? 10);
      const newBounds = calculateBounds(currentCoordinates, distance);

      if (rectangleRef.current === null) {
        setMarketRectangleBounds(newBounds);

        const rectangle = new google.maps.Rectangle({
          bounds: newBounds,
          editable: true,
          draggable: true,
          clickable: false,
        });

        rectangleRef.current = rectangle;
        rectangleRef.current.setMap(map);

        const rectangleList = rectangle.addListener('bounds_changed', () => {
          setHasBoundsChanged((prevState) => !prevState);
        });

        setRectangleListener(rectangleList);
      }
    }

    if (selectedTab !== TabsType.market) {
      clearRectangleRef();
    }
  }, [selectedTab, isLoaded]);

  useEffect(() => {
    if (!isDrawModeOn && selectedTab !== TabsType.market) {
      clearRectangleRef();
    }
  }, [isDrawModeOn]);

  useEffect(() => {
    if(marketPolygons){
      clearRectangleRef();
    } else if(!marketPolygons && selectedTab === TabsType.market) {
      clearRectangleRef();
      generateTriangle();
    }
  },[marketPolygons])

  return { clearRectangleRef };
};

export default useMarketDrawing;
